import { useEffect, useState, useSyncExternalStore, type ReactNode } from 'react';
import { Outlet, useMatch } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { toast } from '@/lib/toast';
import { Button } from '@/components/ui/button';
import { usePageTitle } from '@/hooks/usePageTitle';
import {
  getHubSpotSessionState,
  initializeHubSpotSession,
  openStandaloneApp,
  startHubSpotLogin,
  subscribeHubSpotSession,
} from '@/lib/hubspotSession';

export function HubSpotSessionBoundary({ children }: { children: ReactNode }) {
  const session = useSyncExternalStore(subscribeHubSpotSession, getHubSpotSessionState);
  useEffect(() => {
    void initializeHubSpotSession();
  }, []);
  if (session.status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center" role="status">
        HubSpot-toegang controleren…
      </div>
    );
  }
  return children;
}

export function HubSpotAccessGate() {
  const session = useSyncExternalStore(subscribeHubSpotSession, getHubSpotSessionState);
  const editorRoute = useMatch('/quotes/:id/edit');
  const [opening, setOpening] = useState(false);
  if (session.status === 'ready') {
    // The editor already has this action in its toolbar; avoid an extra row
    // above a full-height document editor.
    if (window.self === window.top || editorRoute) return <Outlet />;
    return (
      <>
        <div className="flex justify-end border-b bg-background px-4 py-2">
          <Button
            variant="ghost"
            size="sm"
            disabled={opening}
            onClick={async () => {
              setOpening(true);
              try {
                await openStandaloneApp();
              } catch (error) {
                toast.error('Openen van de app mislukt.', { error });
              } finally {
                setOpening(false);
              }
            }}
          >
            <ExternalLink className="mr-2 h-4 w-4" />
            {opening ? 'App openen…' : 'Open app in nieuw tabblad'}
          </Button>
        </div>
        <Outlet />
      </>
    );
  }
  // HubSpot does not allow its OAuth screen inside an iframe; the card starts a new session.
  if (window.self !== window.top) {
    return (
      <main className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md text-center space-y-3">
          <h1 className="text-2xl font-semibold">Open deze tool vanuit HubSpot</h1>
          <p className="text-muted-foreground">
            {session.message
              ? `${session.message} Open de tool opnieuw via de Quote Tool-kaart in HubSpot.`
              : 'Ga naar een deal in HubSpot en open de offerte tool via de Quote Tool-kaart.'}
          </p>
        </div>
      </main>
    );
  }
  return <LoginScreen message={session.message} />;
}

function LoginScreen({ message }: { message?: string }) {
  usePageTitle('Inloggen');
  const [redirecting, setRedirecting] = useState(false);
  useEffect(() => {
    // Going back from HubSpot can restore this page from the back/forward cache.
    const reset = (event: PageTransitionEvent) => event.persisted && setRedirecting(false);
    window.addEventListener('pageshow', reset);
    return () => window.removeEventListener('pageshow', reset);
  }, []);
  return (
    <main className="min-h-screen flex items-center justify-center p-6">
      <div className="w-full max-w-sm text-center space-y-6">
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold">Offertetool</h1>
          <p className="text-muted-foreground" role={message ? 'status' : undefined}>
            {message || 'Log in met je HubSpot-account om verder te gaan.'}
          </p>
        </div>
        <Button
          className="w-full"
          disabled={redirecting}
          onClick={async () => {
            setRedirecting(true);
            try {
              await startHubSpotLogin();
            } catch (error) {
              setRedirecting(false);
              toast.error('Inloggen starten is niet gelukt. Probeer het opnieuw.', { error });
            }
          }}
        >
          {redirecting ? 'Doorsturen naar HubSpot…' : 'Inloggen met HubSpot'}
        </Button>
      </div>
    </main>
  );
}
